"use client";

import { useEffect } from "react";
import { klaroConfig } from "@/lib/klaro-config";
import { setGoogleConsentDefaults, updateGoogleConsent } from "@/lib/analytics";

type KlaroManager = {
  consents?: Record<string, boolean>;
  watch: (watcher: { update: (manager: KlaroManager) => void }) => void;
  unwatch: (watcher: { update: (manager: KlaroManager) => void }) => void;
};

export default function KlaroClient() {
  useEffect(() => {
    let mounted = true;
    let manager: KlaroManager | null = null;
    let watcher: { update: (manager: KlaroManager) => void } | null = null;

    const init = async () => {
      const module = await import("klaro");
      const klaro = (module as { default?: typeof module }).default ?? module;
      if (!mounted) return;

      (window as Window & { klaro?: typeof klaro }).klaro = klaro;
      (window as Window & { klaroConfig?: typeof klaroConfig }).klaroConfig =
        klaroConfig;

      if (typeof klaro.setup === "function") {
        klaro.setup(klaroConfig);
      } else if (typeof (module as { setup?: typeof klaro.setup }).setup === "function") {
        (module as { setup: typeof klaro.setup }).setup(klaroConfig);
      }

      setGoogleConsentDefaults();

      manager = klaro.getManager?.(klaroConfig) as KlaroManager | undefined;
      if (!manager) return;

      const syncConsent = (mgr: KlaroManager) => {
        updateGoogleConsent({
          analytical: mgr.consents?.analytical,
          marketing: mgr.consents?.marketing,
        });
      };

      syncConsent(manager);

      watcher = {
        update: (updatedManager: KlaroManager) => {
          if (!mounted) return;
          syncConsent(updatedManager);
        },
      };

      manager.watch(watcher);
    };

    void init();

    return () => {
      mounted = false;
      if (manager && watcher) {
        manager.unwatch(watcher);
      }
    };
  }, []);

  return null;
}
